import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { VOYAGE_SEARCH_CREDITS } from 'src/app/services/operators/voyageradar-credit-actions';
import { VesselSearchHit } from '../../shared/contracts/vessel.types';

/**
 * Voyage Radar vessel search — drives the magnifier button and its drawer.
 *
 * This used to be `app-voyage-search`, a 420px pill floating top-centre over the
 * map with its own dropdown, its own backdrop and its own 360-line stylesheet.
 * The behaviour is unchanged; what moved is where it lives. Every other thing
 * this sensor can do is a toolbar button opening a drawer, and search was the
 * one exception — a second idiom for the same class of act, occupying map at
 * all times to do it.
 *
 * WHY A CONTROLLER AT ALL, when the old component held its own state: the drawer
 * builds a FRESH panel instance every time it opens (see the note on
 * `TrackOptionsPanelComponent.selected`, which relies on exactly that). Panel-
 * local state would therefore be discarded on every close — and the state here
 * includes `worldwide`, `_cache` and `searchedQuery`, which describe results the
 * user PAID a credit for. Losing those on a close would silently re-charge for a
 * query already bought. So the state outlives the panel, and the panel is a
 * rendering of it.
 *
 * Two prices are NOT involved here, unlike the nearby drawer: locating a result
 * is free, because the search response already carries the position. The one
 * paid act is widening the query to the worldwide registry.
 *
 * Sibling of `VoyagePortCallsService` and `VoyageNearbyService` in shape —
 * `providedIn: 'root'` PLUS an entry in the host component's `providers` (see
 * the note there; a root-only service resolves its dependencies in the root
 * injector and cannot see the component's) — but NOT in substance: it has no
 * subject hull, because a search is about a question rather than about a vessel.
 */

/** One worldwide result row, as the endpoint returns it (id = MMSI). */
export interface WorldwideRow {
  id: number | string;
  shipName?: string;
  shipType?: string;
  flag?: string;
  imoNumber?: number;
  callSign?: string;
  navigationalStatus?: string;
  time_utc?: string;
  latitude?: number;
  longitude?: number;
  waterBody?: string;
}

export interface SearchState {
  /** What the box holds. Survives a close, so reopening resumes the same question. */
  query: string;
  /**
   * Free matches over what is already in the browser — the drawn snapshot, any
   * loaded frame, and the task's kept vessels. Recomputed from the map on every
   * keystroke and on every open, never stored across a map change: a hit list
   * held over a snapshot swap would offer vessels that are no longer there.
   */
  localHits: VesselSearchHit[];
  /** The paid block. Appended under its own label; never replaces the free list. */
  worldwide: WorldwideRow[];
  /** Upstream total when it exceeds what was returned — "top N of M". */
  worldwideTotal: number | null;
  /**
   * The query the worldwide block belongs to, normalised. Editing the box
   * re-arms the paid row, so a stale block can never be read as an answer to a
   * question it was not asked.
   */
  searchedQuery: string | null;
  searching: boolean;
  /** Inline, in the panel — never a modal. A search miss is part of searching. */
  searchError: string | null;

  // ── Facts about the map, pushed by the map controller ─────────────────────
  /** Nothing drawn. The panel says what to do about it rather than vanishing. */
  mapEmpty: boolean;
  /** The task has saved refreshes, so the empty case names LOAD, not REFRESH. */
  hasHistory: boolean;
  /** Vessels in the drawn snapshot. */
  vesselCount: number;
  /** How many of them broadcast no name — they match by MMSI only. */
  unnamed: number;

  searchCredits: number;
  creditsRemaining: number | null;
  /** TOI over — nothing here can be bought again. See `hasVoyageToiFinished`. */
  toiEnded: boolean;
}

export const EMPTY_SEARCH_STATE: SearchState = {
  query: '',
  localHits: [],
  worldwide: [],
  worldwideTotal: null,
  searchedQuery: null,
  searching: false,
  searchError: null,
  mapEmpty: true,
  hasHistory: false,
  vesselCount: 0,
  unnamed: 0,
  searchCredits: VOYAGE_SEARCH_CREDITS,
  creditsRemaining: null,
  toiEnded: false,
};

/** Below this the box does not filter — two characters match half a fleet. */
export const SEARCH_MIN_CHARS = 2;

@Injectable({ providedIn: 'root' })
export class VoyageSearchService {
  private _state$ = new BehaviorSubject<SearchState>({ ...EMPTY_SEARCH_STATE });
  readonly state$: Observable<SearchState> = this._state$.asObservable();

  /**
   * Session cache of paid answers, keyed by normalised query.
   *
   * The whole reason this class exists rather than panel state. Re-typing a
   * query already bought re-attaches its results instantly and free, and the
   * panel says so — so a user comparing two vessels does not pay twice to look
   * back at the first. Cleared only by `reset()`, on a task switch, because the
   * results belong to the sensor that was charged for them.
   */
  private _cache = new Map<string, { results: WorldwideRow[]; total: number | null }>();

  // ── Callbacks the map controller assigns in attach() ────────────────────────

  /** Opens the drawer. Null when no control is mounted — see `openAndFocus`. */
  onRequestOpen: (() => void) | null = null;
  /**
   * The FREE half: matches over what the browser already holds. Synchronous and
   * in-memory, exactly like the port-call drawer's `onLookupStored`, which is
   * why it is a lookup rather than a fetch.
   */
  onSearchLoaded: ((query: string) => VesselSearchHit[]) | null = null;
  /** Draw a free local hit and select it. */
  onPickLocal: ((hit: VesselSearchHit) => void) | null = null;
  /** Draw a bought worldwide row and select it. Free — the row carries a fix. */
  onPickWorldwide: ((row: WorldwideRow, query: string) => void) | null = null;
  /** The one paid act. Resolves through `setWorldwide` / `setSearchFailed`. */
  onSearchWorldwide: ((query: string) => void) | null = null;

  // ── Queries ─────────────────────────────────────────────────────────────────

  snapshot(): SearchState {
    return this._state$.value;
  }

  /** Enough typed to filter on. */
  get typed(): boolean {
    return this._state$.value.query.trim().length >= SEARCH_MIN_CHARS;
  }

  /** The worldwide block belongs to the CURRENT query, not to an edited one. */
  get searched(): boolean {
    const s = this._state$.value;
    return s.searchedQuery !== null && s.searchedQuery === normalize(s.query);
  }

  // ── Input ───────────────────────────────────────────────────────────────────

  /**
   * The box changed. Re-filters the free list and re-attaches a cached paid
   * block if this exact query has been bought before; otherwise the paid block
   * clears and the paid row re-arms.
   */
  setQuery(query: string): void {
    const q = String(query ?? '');
    const key = normalize(q);
    const cached = this._cache.get(key);
    this.patch({
      query: q,
      searchError: null,
      localHits: this.localHitsFor(q),
      worldwide: cached ? cached.results : [],
      worldwideTotal: cached ? cached.total : null,
      searchedQuery: cached ? key : null,
    });
  }

  /**
   * The drawer opened. Re-runs the free filter against the map AS IT IS NOW —
   * the snapshot may have been swapped, refreshed or cleared since the last
   * open, and offering a hit for a vessel no longer drawn would locate nothing.
   * The paid block is left alone: it was bought, and it is not about the map.
   */
  refreshLocalHits(): void {
    this.patch({ localHits: this.localHitsFor(this._state$.value.query) });
  }

  clear(): void {
    this.setQuery('');
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * Open the drawer for the `/` shortcut, whatever else has focus.
   *
   * Guarded on `onRequestOpen` for the same reason the port-call drawer is:
   * with no control mounted there is nowhere to show a result, and a keystroke
   * that appears to do nothing is better than one that half-works.
   */
  openAndFocus(): void {
    if (!this.onRequestOpen) return;
    this.refreshLocalHits();
    this.onRequestOpen();
  }

  pickLocal(hit: VesselSearchHit): void {
    if (!hit) return;
    this.onPickLocal?.(hit);
  }

  pickWorldwide(row: WorldwideRow): void {
    if (!row) return;
    this.onPickWorldwide?.(row, this._state$.value.query.trim());
  }

  /**
   * Spend the credit and widen the query to the registry.
   *
   * Every guard here also exists on the button that calls it; they are repeated
   * because this is also reachable from `Enter` handling and from any future
   * caller, and a spend gate that lives only in a template is one edit away
   * from not existing. The `searched` guard is the one that matters most: it
   * makes a second press of an already-bought query a no-op rather than a
   * second charge.
   */
  searchWorldwide(): void {
    const s = this._state$.value;
    const q = s.query.trim();
    if (q.length < SEARCH_MIN_CHARS || s.searching || this.searched) return;
    if (!this.onSearchWorldwide) return;
    this.patch({ searching: true, searchError: null });
    this.onSearchWorldwide(q);
  }

  // ── Resolutions, called by the map controller ───────────────────────────────

  /**
   * A paid search came back. An EMPTY result is still an answer and still gets
   * cached: the server does not charge for it (the copy says so), and caching it
   * stops the paid row re-arming on a query already known to have no match.
   */
  setWorldwide(query: string, results: WorldwideRow[], total: number | null): void {
    const rows = Array.isArray(results) ? results : [];
    const key = normalize(query);
    this._cache.set(key, { results: rows, total });
    this.patch({
      searching: false,
      worldwide: rows,
      worldwideTotal: total,
      searchedQuery: key,
      searchError: rows.length
        ? null
        : 'Nothing worldwide for that — check the spelling, or try the MMSI, IMO or ' +
          'call sign. You were not charged.',
    });
  }

  /**
   * Inline failure. NOT cached — a failed call must be retryable.
   *
   * With NO message it is a bare reset, which is what a caller that raised a
   * modal instead needs: the spinner has to come down either way, and the same
   * sentence in both places is the one that gets read in neither. The nearby
   * and port-call drawers spell this `setFailed()`; this one carries a message
   * as well because a search MISS is reported here rather than in a modal.
   */
  setSearchFailed(message: string | null = null): void {
    this.patch({ searching: false, searchError: message });
  }

  // ── Pushed by the map controller ────────────────────────────────────────────

  /** Price, balance and TOI, the same way every other priced drawer is told. */
  setCredits(creditsRemaining: number | null, searchCredits: number, toiEnded: boolean): void {
    this.patch({ creditsRemaining, searchCredits, toiEnded });
  }

  /**
   * What the map currently holds. Four facts the panel needs and a toolbar
   * control cannot reach: `ToolbarControlContext` carries no map and no sensor,
   * and `VoyageMapController` is constructed by the host rather than injected,
   * so it is not reachable through DI either.
   *
   * Re-filters the free list as a side effect, because every one of these facts
   * changing means the set of searchable vessels changed too.
   */
  setMapContext(ctx: {
    mapEmpty: boolean;
    hasHistory: boolean;
    vesselCount: number;
    unnamed: number;
  }): void {
    this.patch({
      mapEmpty: !!ctx.mapEmpty,
      hasHistory: !!ctx.hasHistory,
      vesselCount: Number(ctx.vesselCount) || 0,
      unnamed: Number(ctx.unnamed) || 0,
      localHits: this.localHitsFor(this._state$.value.query),
    });
  }

  /**
   * Task switch. The cache goes with it — those results were bought against a
   * particular sensor's wallet and describe a question asked in that context.
   */
  reset(): void {
    this._cache.clear();
    this._state$.next({ ...EMPTY_SEARCH_STATE });
  }

  // ── Internals ───────────────────────────────────────────────────────────────

  private localHitsFor(query: string): VesselSearchHit[] {
    if (String(query ?? '').trim().length < SEARCH_MIN_CHARS) return [];
    return this.onSearchLoaded?.(query) ?? [];
  }

  private patch(part: Partial<SearchState>): void {
    this._state$.next({ ...this._state$.value, ...part });
  }
}

function normalize(q: string): string {
  return String(q ?? '').trim().toLowerCase();
}
